/**
 * Drucker-Verwaltung - QZ Tray Integration
 * 
 * Features:
 * - QZ Tray Desktop App verbinden
 * - Drucker konfigurieren
 * - Testdruck senden
 * - QZ Tray Download-Anleitung
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Printer, Download, RefreshCw, CheckCircle, XCircle,
  Settings, Wifi, WifiOff, AlertTriangle, Play,
  Monitor, Smartphone, Cloud, ArrowLeft, HelpCircle,
  ExternalLink, Copy, Check, Loader2
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import useQZTray from '@/hooks/useQZTray';

// Drucker-Rollen
const PRINTER_ROLES = {
  customer: { label: 'Kundenbon', description: 'Druckt Kunden-Bons' },
  kitchen: { label: 'Küche', description: 'Druckt Küchen-Tickets' },
  bar: { label: 'Bar', description: 'Druckt Bar-Tickets' },
  a4: { label: 'A4 (Rechnungen)', description: 'Druckt Rechnungen & Berichte' }
};

export default function PrinterManagement() {
  const navigate = useNavigate();
  const [sessionToken, setSessionToken] = useState(null);
  const [printerConfig, setPrinterConfig] = useState({
    customer: '',
    kitchen: '',
    bar: '',
    a4: ''
  });
  const [testPrinting, setTestPrinting] = useState(null);
  const [copiedText, setCopiedText] = useState(false);
  const [orderPrinting, setOrderPrinting] = useState(false);
  const [lastOrders, setLastOrders] = useState([]);
  
  // QZ Tray Hook
  const {
    isConnected,
    isConnecting,
    qzLoaded,
    error,
    printers,
    selectedPrinter,
    selectPrinter,
    refreshPrinters,
    connect,
    disconnect,
    sendTestPrint,
    printThermalReceipt
  } = useQZTray();

  // Token laden
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/login');
      return;
    }
    setSessionToken(token);
    
    // Gespeicherte Drucker-Konfiguration laden
    const savedConfig = localStorage.getItem('qz-tray-printer-config');
    if (savedConfig) {
      try {
        setPrinterConfig(JSON.parse(savedConfig));
      } catch (e) {
        console.error('Fehler beim Laden der Drucker-Konfiguration:', e);
      }
    }
  }, [navigate]);

  // Auto-Connect wenn QZ geladen
  useEffect(() => {
    if (qzLoaded && !isConnected && !isConnecting) {
      // Automatisch verbinden nach kurzer Verzögerung
      const timer = setTimeout(() => {
        connect();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [qzLoaded, isConnected, isConnecting, connect]);

  // Drucker-Rolle zuweisen
  const assignPrinterRole = useCallback((role, printerName) => {
    const newConfig = { ...printerConfig, [role]: printerName };
    setPrinterConfig(newConfig);
    localStorage.setItem('qz-tray-printer-config', JSON.stringify(newConfig));
    toast.success(`${PRINTER_ROLES[role].label} zugewiesen: ${printerName || 'Keine'}`);
  }, [printerConfig]);

  // Testdruck senden
  const handleTestPrint = useCallback(async (role) => {
    const printerName = printerConfig[role];
    if (!printerName) {
      toast.error('Kein Drucker für diese Rolle zugewiesen');
      return;
    }
    
    setTestPrinting(role);
    
    try {
      await sendTestPrint(printerName);
      toast.success(`Testdruck an "${printerName}" gesendet!`);
    } catch (err) {
      toast.error(`Druckfehler: ${err.message}`);
    } finally {
      setTestPrinting(null);
    }
  }, [printerConfig, sendTestPrint]);

  // Letzte Bestellungen laden
  const loadLastOrders = useCallback(async () => {
    if (!sessionToken) return;
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/transactions?limit=5`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLastOrders(data.transactions || []);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Bestellungen:', err);
    }
  }, [sessionToken]);

  // Bestellungen laden wenn verbunden
  useEffect(() => {
    if (isConnected && sessionToken) {
      loadLastOrders();
    }
  }, [isConnected, sessionToken, loadLastOrders]);

  // Echte Bestellung drucken
  const printOrder = useCallback(async (orderId, receiptType = 'customer') => {
    const printerName = printerConfig[receiptType] || printerConfig.customer;
    if (!printerName) {
      toast.error('Kein Drucker für Kundenbons zugewiesen');
      return;
    }
    
    setOrderPrinting(true);
    
    try {
      // Receipt HTML vom Backend holen
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(
        `${backendUrl}/api/qz-tray/receipt/${orderId}?receipt_type=${receiptType}`,
        { headers: { 'Authorization': `Bearer ${sessionToken}` } }
      );
      
      if (!response.ok) {
        throw new Error('Bestellung nicht gefunden');
      }
      
      const data = await response.json();
      
      // An QZ Tray senden
      await printThermalReceipt(data.html, printerName);
      toast.success(`Bon gedruckt auf "${printerName}"!`);
    } catch (err) {
      toast.error(`Druckfehler: ${err.message}`);
    } finally {
      setOrderPrinting(false);
    }
  }, [printerConfig, sessionToken, printThermalReceipt]);

  // QZ Tray Download Link
  const openQZTrayDownload = () => {
    window.open('https://qz.io/download/', '_blank');
  };

  // Text kopieren
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(true);
      toast.success('In Zwischenablage kopiert');
      setTimeout(() => setCopiedText(false), 2000);
    } catch (err) {
      toast.error('Kopieren fehlgeschlagen');
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white" data-testid="printer-management">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-gray-400 hover:text-white"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Printer className="w-5 h-5 text-teal-500" />
                Drucker-Verwaltung
              </h1>
              <p className="text-sm text-gray-400">
                QZ Tray Drucksystem
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={refreshPrinters}
              disabled={!isConnected}
              className="border-gray-600"
              data-testid="refresh-printers-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Aktualisieren
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        
        {/* QZ Tray Status Card */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  isConnected ? 'bg-teal-500/20' : isConnecting ? 'bg-yellow-500/20' : 'bg-red-500/20'
                }`}>
                  {isConnecting ? (
                    <Loader2 className="w-6 h-6 text-yellow-500 animate-spin" />
                  ) : isConnected ? (
                    <Wifi className="w-6 h-6 text-teal-500" />
                  ) : (
                    <WifiOff className="w-6 h-6 text-red-500" />
                  )}
                </div>
                <div>
                  <CardTitle className="text-white">
                    QZ Tray {isConnecting ? 'Verbindet...' : isConnected ? 'Verbunden' : 'Offline'}
                  </CardTitle>
                  <CardDescription>
                    {isConnected 
                      ? `${printers.length} Drucker gefunden` 
                      : error || 'QZ Tray Desktop App nicht gefunden'
                    }
                  </CardDescription>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {isConnected ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={disconnect}
                    className="border-gray-600"
                    data-testid="disconnect-btn"
                  >
                    Trennen
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={connect}
                    disabled={isConnecting || !qzLoaded}
                    className="bg-teal-600 hover:bg-teal-700"
                    data-testid="connect-btn"
                  >
                    {isConnecting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Verbindet...
                      </>
                    ) : (
                      'Verbinden'
                    )}
                  </Button>
                )}
                <Badge variant={isConnected ? 'default' : 'destructive'}>
                  {isConnected ? 'Online' : 'Offline'}
                </Badge>
              </div>
            </div>
          </CardHeader>
          
          {!isConnected && !isConnecting && (
            <CardContent>
              <Alert className="bg-yellow-500/10 border-yellow-500/30">
                <AlertTriangle className="w-4 h-4 text-yellow-500" />
                <AlertTitle className="text-yellow-500">QZ Tray nicht verbunden</AlertTitle>
                <AlertDescription className="text-gray-300">
                  Um drucken zu können, muss die QZ Tray App auf Ihrem Computer laufen.
                  
                  <div className="mt-4 flex gap-3 flex-wrap">
                    <Button 
                      size="sm" 
                      onClick={openQZTrayDownload}
                      className="bg-teal-600 hover:bg-teal-700"
                      data-testid="download-qz-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      QZ Tray herunterladen
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={connect}
                      className="border-gray-600"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Erneut verbinden
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            </CardContent>
          )}
        </Card>

        {/* Drucker Tabs */}
        <Tabs defaultValue="config" className="w-full">
          <TabsList className="bg-gray-800 border border-gray-700">
            <TabsTrigger value="config" className="data-[state=active]:bg-gray-700">
              <Settings className="w-4 h-4 mr-2" />
              Konfiguration
            </TabsTrigger>
            <TabsTrigger value="setup" className="data-[state=active]:bg-gray-700">
              <HelpCircle className="w-4 h-4 mr-2" />
              Einrichtung
            </TabsTrigger>
          </TabsList>

          {/* Drucker Konfiguration */}
          <TabsContent value="config" className="space-y-4">
            {!isConnected ? (
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="py-12 text-center">
                  <Printer className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">
                    QZ Tray nicht verbunden
                  </h3>
                  <p className="text-gray-400 mb-4">
                    Verbinden Sie sich mit QZ Tray, um Drucker zu konfigurieren.
                  </p>
                  <Button
                    onClick={connect}
                    disabled={isConnecting}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    Mit QZ Tray verbinden
                  </Button>
                </CardContent>
              </Card>
            ) : printers.length === 0 ? (
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="py-12 text-center">
                  <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">
                    Keine Drucker gefunden
                  </h3>
                  <p className="text-gray-400 mb-4">
                    QZ Tray ist verbunden, aber es wurden keine Drucker erkannt.
                    Stellen Sie sicher, dass Ihre Drucker eingeschaltet und verbunden sind.
                  </p>
                  <Button
                    onClick={refreshPrinters}
                    variant="outline"
                    className="border-gray-600"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Drucker neu laden
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {Object.entries(PRINTER_ROLES).map(([role, info]) => {
                  const assignedPrinter = printerConfig[role];
                  
                  return (
                    <Card 
                      key={role} 
                      className={`bg-gray-800 border-gray-700 ${
                        assignedPrinter ? 'border-l-4 border-l-teal-500' : ''
                      }`}
                      data-testid={`printer-card-${role}`}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              assignedPrinter ? 'bg-teal-500/20' : 'bg-gray-700'
                            }`}>
                              <Printer className={`w-5 h-5 ${
                                assignedPrinter ? 'text-teal-500' : 'text-gray-500'
                              }`} />
                            </div>
                            <div>
                              <CardTitle className="text-white text-base">
                                {info.label}
                              </CardTitle>
                              <CardDescription className="text-sm">
                                {info.description}
                              </CardDescription>
                            </div>
                          </div>
                          
                          {assignedPrinter && (
                            <CheckCircle className="w-5 h-5 text-teal-500" />
                          )}
                        </div>
                      </CardHeader>
                      
                      <CardContent className="space-y-3">
                        <Select 
                          value={assignedPrinter || 'none'} 
                          onValueChange={(value) => assignPrinterRole(role, value === 'none' ? '' : value)}
                        >
                          <SelectTrigger className="bg-gray-700 border-gray-600" data-testid={`printer-select-${role}`}>
                            <SelectValue placeholder="Drucker auswählen..." />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-700">
                            <SelectItem value="none" className="text-gray-400">
                              -- Nicht zugewiesen --
                            </SelectItem>
                            {printers.map((printer) => (
                              <SelectItem key={printer} value={printer}>
                                {printer}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full border-gray-600"
                          onClick={() => handleTestPrint(role)}
                          disabled={!assignedPrinter || testPrinting === role}
                          data-testid={`test-print-${role}`}
                        >
                          {testPrinting === role ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Play className="w-4 h-4 mr-2" />
                          )}
                          Testdruck
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Letzte Bestellungen drucken */}
            {isConnected && printerConfig.customer && (
              <Card className="bg-gray-800 border-gray-700 mt-6">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Printer className="w-5 h-5 text-teal-500" />
                    Bestellung drucken
                  </CardTitle>
                  <CardDescription>
                    Wählen Sie eine Bestellung zum Drucken aus
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {lastOrders.length === 0 ? (
                    <div className="text-center py-4 text-gray-400">
                      Keine Bestellungen gefunden
                      <Button
                        size="sm"
                        variant="outline"
                        className="ml-4 border-gray-600"
                        onClick={loadLastOrders}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Neu laden
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {lastOrders.map((order) => (
                        <div 
                          key={order.transaction_id || order._id}
                          className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg"
                        >
                          <div>
                            <div className="font-medium text-white">
                              {order.receipt_no || `#${(order.transaction_id || '').slice(0, 8)}`}
                            </div>
                            <div className="text-sm text-gray-400">
                              {order.items?.length || 0} Artikel • {(order.total_gross || 0).toFixed(2)} €
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => printOrder(order.transaction_id, 'customer')}
                              disabled={orderPrinting}
                              className="bg-teal-600 hover:bg-teal-700"
                              data-testid={`print-order-${order.transaction_id}`}
                            >
                              {orderPrinting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Printer className="w-4 h-4" />
                              )}
                              <span className="ml-2">Drucken</span>
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Setup Anleitung */}
          <TabsContent value="setup">
            <div className="space-y-4">
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">QZ Tray einrichten</CardTitle>
                  <CardDescription>
                    QZ Tray ist eine professionelle Drucklösung, die Drucken direkt aus dem Browser ermöglicht.
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="space-y-6">
                  {/* Step 1 */}
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-teal-600 flex items-center justify-center flex-shrink-0">
                      1
                    </div>
                    <div>
                      <h4 className="font-medium text-white">QZ Tray herunterladen</h4>
                      <p className="text-sm text-gray-400 mt-1">
                        Laden Sie QZ Tray von der offiziellen Website herunter und installieren Sie es.
                      </p>
                      <Button 
                        size="sm" 
                        className="mt-2 bg-teal-600 hover:bg-teal-700"
                        onClick={openQZTrayDownload}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        qz.io öffnen
                      </Button>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-teal-600 flex items-center justify-center flex-shrink-0">
                      2
                    </div>
                    <div>
                      <h4 className="font-medium text-white">Installer ausführen</h4>
                      <p className="text-sm text-gray-400 mt-1">
                        Starten Sie die heruntergeladene Datei und folgen Sie den Installationsanweisungen.
                        Für Windows wählen Sie die .exe-Datei, für Mac die .pkg-Datei.
                      </p>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-teal-600 flex items-center justify-center flex-shrink-0">
                      3
                    </div>
                    <div>
                      <h4 className="font-medium text-white">QZ Tray starten</h4>
                      <p className="text-sm text-gray-400 mt-1">
                        Nach der Installation startet QZ Tray automatisch und erscheint als Icon in der Taskleiste.
                        Das Icon zeigt den Status: Grün = bereit, Grau = keine Verbindung.
                      </p>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-teal-600 flex items-center justify-center flex-shrink-0">
                      4
                    </div>
                    <div>
                      <h4 className="font-medium text-white">Mit Cash-X verbinden</h4>
                      <p className="text-sm text-gray-400 mt-1">
                        Klicken Sie oben auf "Verbinden", um Cash-X mit QZ Tray zu verbinden.
                        Bei der ersten Verbindung fragt QZ Tray um Erlaubnis - klicken Sie auf "Erlauben".
                      </p>
                    </div>
                  </div>

                  {/* Step 5 */}
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="font-medium text-white">Drucker zuweisen</h4>
                      <p className="text-sm text-gray-400 mt-1">
                        Weisen Sie Ihren Druckern die entsprechenden Rollen zu (Kunde, Küche, Bar, A4).
                        Senden Sie einen Testdruck, um die Konfiguration zu überprüfen.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* FAQ */}
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Häufige Fragen</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-medium text-white">Warum QZ Tray statt des alten Print-Agents?</h4>
                    <p className="text-sm text-gray-400 mt-1">
                      QZ Tray ist eine bewährte, stabile Lösung, die von tausenden Unternehmen weltweit eingesetzt wird.
                      Sie bietet bessere Zuverlässigkeit, automatische Updates und professionellen Support.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-white">Brauche ich ein Zertifikat für Silent Printing?</h4>
                    <p className="text-sm text-gray-400 mt-1">
                      Im Demo-Modus erscheint bei jedem Druck ein Bestätigungsdialog. 
                      Für automatisches Drucken ohne Dialog können Sie ein Zertifikat von qz.io erwerben.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-white">Funktioniert QZ Tray auf mehreren Computern?</h4>
                    <p className="text-sm text-gray-400 mt-1">
                      Ja, Sie können QZ Tray auf jedem Computer installieren, der drucken soll.
                      Die Drucker werden automatisch erkannt.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Architektur-Erklärung */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">So funktioniert das Drucksystem</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 p-4">
              {/* Browser */}
              <div className="text-center">
                <div className="w-16 h-16 rounded-xl bg-teal-500/20 flex items-center justify-center mx-auto mb-2">
                  <Monitor className="w-8 h-8 text-teal-500" />
                </div>
                <p className="text-sm font-medium">Cash-X im Browser</p>
                <p className="text-xs text-gray-500">(WebSocket)</p>
              </div>
              
              {/* Arrow */}
              <div className="text-gray-600 text-2xl">→</div>
              
              {/* QZ Tray */}
              <div className="text-center">
                <div className="w-16 h-16 rounded-xl bg-purple-500/20 flex items-center justify-center mx-auto mb-2">
                  <Settings className="w-8 h-8 text-purple-500" />
                </div>
                <p className="text-sm font-medium">QZ Tray</p>
                <p className="text-xs text-gray-500">Desktop App</p>
              </div>
              
              {/* Arrow */}
              <div className="text-gray-600 text-2xl">→</div>
              
              {/* Printer */}
              <div className="text-center">
                <div className="w-16 h-16 rounded-xl bg-orange-500/20 flex items-center justify-center mx-auto mb-2">
                  <Printer className="w-8 h-8 text-orange-500" />
                </div>
                <p className="text-sm font-medium">Drucker</p>
                <p className="text-xs text-gray-500">USB / Netzwerk</p>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-gray-700/50 rounded-lg text-sm text-gray-300">
              <strong className="text-white">Vorteile:</strong>
              <ul className="mt-2 space-y-1 text-gray-400">
                <li>✓ Funktioniert mit allen Druckern (Thermobondrucker, Laserdrucker, etc.)</li>
                <li>✓ Kein manueller Neustart nötig - QZ Tray läuft als Windows-Dienst</li>
                <li>✓ Professioneller Support und regelmäßige Updates</li>
                <li>✓ Funktioniert auch offline (lokale Verbindung)</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
